// From CPRD-820, create pattern for websockets in the frontend
// For ready state and other websocket specific built in APIs checkout
// https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/readyState

import { toast } from 'vue3-toastify';
import { defineStore, acceptHMRUpdate } from 'pinia';
import { useGamePlanStore } from './useGamePlans';
import { useUsersStore } from './useUsers';
import { useRbac } from '@/composables/useRbac';
import { useWorkItemsStore } from './useWorkItems';
import router from '@/router';
import { getUserInitials } from '@/utils/user';
import { getIdToken } from '@/utils/auth';
import { useExportNotifications } from '@/composables/useExportNotifications';
import { useExports } from './useExports';
import { useSkillBadgesStore } from './useSkillBadges';

export const WEBSOCKET_STATES = {
  CONNECTING: 0,
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3,
};

export const useWebSockets = defineStore('useWebSockets', {
  state: () => ({
    connection: undefined,
    readyState: undefined,
    token: undefined,
    baseURL: import.meta.env.VITE_WEB_SOCKET_API_GATEWAY_ENDPOINT,
    backoff: 2000, // use for exponential backoff when trying to reconnect on error
    loading: false,
    ping: undefined,
    INTERVAL: 5 * 60 * 1000,
    MAX_BACKOFF: 1 * 60 * 1000,
    connectTimeout: undefined,
    _closing: false,
    _unloadHandler: undefined,
  }),
  getters: {
    getEditGamePlan() {
      return this.editGamePlan;
    },
    /* Returns true if loading, connecting, or connected */
    checkConnection() {
      // 0/1 means connecting/connected
      if (
        this.readyState == WEBSOCKET_STATES.CONNECTING ||
        this.readyState == WEBSOCKET_STATES.OPEN ||
        this.loading
      ) {
        return true;
      }
      return false;
    },
    isConnected() {
      return this.readyState === WEBSOCKET_STATES.OPEN;
    },
  },
  actions: {
    async websocketEventRouter(message) {
      const { hasWriteRole } = useRbac();
      const gameplan_store = useGamePlanStore();
      const workItems_store = useWorkItemsStore();
      const users_store = useUsersStore();
      const game_plan_events = [
        'work_strategy_locked',
        'draft_work_strategy_saved',
        'work_strategy_unlocked',
        'work_strategy_updated',
        'work_strategy_failed',
        'adm_draft_ws_canceled',
        'inb_feed_work_strategy',
      ];
      const claim_status_events = [
        'claim_status_published',
        'claim_status_not_available',
      ];
      const user_events = ['auxo_user_deactivated', 'auxo_user_photo_updated'];
      const export_events = ['export_complete', 'export_failed'];
      const skill_badge_events = ['skill_badge_achieved'];

      const key = message?.event_type;

      if (game_plan_events.includes(key) && hasWriteRole('EDIT_GAMEPLAN')) {
        // Game Plan Related Events
        if (
          key == 'work_strategy_unlocked' ||
          key == 'work_strategy_updated' ||
          key == 'adm_draft_ws_canceled' ||
          key == 'work_strategy_failed'
        ) {
          if (key == 'work_strategy_failed') {
            gameplan_store.showFailToast();
          }
          gameplan_store.setLockStatusAndMessage(false);
          gameplan_store.resetWorklists();
        }

        if (key == 'work_strategy_locked') {
          gameplan_store.setLockStatusAndMessage(
            true,
            `${message.payload.owner} is currently editing the Game Plan.`
          );
        }

        if (
          key == 'draft_work_strategy_saved' ||
          key == 'inb_feed_work_strategy'
        ) {
          gameplan_store.setLockStatusAndMessage(
            true,
            `The Game Plan strategy is being recalculated. Edit Mode is unavailable.`,
            true
          );
        }
      } else if (claim_status_events.includes(key)) {
        // Claim Status Related Events
        if (key == 'claim_status_published') {
          if (
            router.currentRoute.value.fullPath ===
            `/lead/${message?.payload.work_item_id}`
          ) {
            workItems_store.isClaimStatusLoading = false;
            workItems_store.fetchWorkItemActivities(
              message?.payload.work_item_id
            );
          }
        }

        if (key == 'claim_status_not_available') {
          if (
            router.currentRoute.value.fullPath ===
            `/lead/${message?.payload.work_item_id}`
          ) {
            workItems_store.isClaimStatusLoading = false;
            toast.error(`Claim Status check not available, try again later`);
          }
        }
      } else if (user_events.includes(key)) {
        // User Management Related Events
        if (key == 'auxo_user_deactivated') {
          users_store.logOut();
        }
        if (key === 'auxo_user_photo_updated') {
          const user_detail = await users_store.getUserByEmail(
            users_store.activeUser.email
          );
          users_store.getAllUsers();
          users_store.activeUser.photo_url = user_detail.photo_url || 'none';
          users_store.activeUser.initials = getUserInitials(user_detail);
        }
      } else if (export_events.includes(key)) {
        // Export Related Events
        const { showExportSuccessToast, showExportErrorToast } =
          useExportNotifications();
        const export_store = useExports();
        if (key === 'export_complete') {
          // Update the UI
          export_store.setExportReady(
            message.payload?.service_type,
            message.payload?.export_id,
            message.payload?.presigned_url,
            message.payload?.expiration_timest,
            message.payload?.export_type
          );

          // Update the export record
          export_store.updateExport(message.payload?.export_id, {
            status: 'completed',
            fileName: message.payload?.export_name,
            url: message.payload?.presigned_url,
            expirationTimest: message.payload?.expiration_timest,
          });

          // Show the success toast
          showExportSuccessToast(
            message.payload?.alias || message.payload?.export_name,
            message.payload?.presigned_url
          );
        }

        if (key === 'export_failed') {
          showExportErrorToast(
            message.payload?.alias || message.payload?.export_name,
            message.payload.errorMessage
          );

          // Update the export record
          export_store.updateExport(message.payload?.export_id, {
            status: 'failed',
            expirationTimest: message.payload?.expiration_timest,
          });

          export_store.setExportStatus(
            message.payload?.service_type,
            message.payload?.export_id,
            'failed',
            message.payload?.export_type
          );
        }
      } else if (skill_badge_events.includes(key)) {
        const skill_badges_store = useSkillBadgesStore();
        let achievement = {
          badge_id: message.payload.badge_id,
          tier_number: message.payload.tier_number,
          badge_name: message.payload.badge_name,
          badge_description: message.payload.badge_description,
          achievement_date: message.payload.achievement_date,
          auxo_user_id: message.payload.reply_to,
        };
        skill_badges_store.badgeNotifications.push(achievement);
      }
    },
    async keepalive() {
      const message = {
        action: 'ping',
      };
      this.ping = setInterval(() => {
        try {
          this.connection.send(JSON.stringify(message));
        } catch {
          console.debug('error');
        }
      }, this.INTERVAL);
    },
    async reconnect() {
      this.backoff = Number(this.backoff) * 2;
      clearInterval(this.ping);
      console.debug('Attempting to reconnect.');
      console.debug('Backoff set to: ' + this.backoff);
      console.debug('The connection url is: ' + this.baseURL);
      await this.connect();
    },
    async connect() {
      if (this._closing || this.checkConnection) return;
      this.loading = true;

      this.connectTimeout = setTimeout(
        async () => {
          if (this._closing) {
            this.loading = false;
            return;
          }

          const idToken = await getIdToken();

          // Guard: logout may have been called while awaiting getIdToken()
          if (this._closing) {
            this.loading = false;
            return;
          }

          let sessionId = sessionStorage.getItem('ws_session_id');
          if (!sessionId) {
            sessionId = crypto.randomUUID();
            sessionStorage.setItem('ws_session_id', sessionId);
          }
          const url =
            this.baseURL + '?idToken=' + idToken + '&sessionId=' + sessionId;
          const ws = new WebSocket(url);

          // Assign immediately so close() can inspect readyState
          this.connection = ws;
          this.readyState = ws.readyState;

          // Close the socket synchronously on page unload (refresh, navigation)
          // so the backend receives a close frame and fires $disconnect.
          if (this._unloadHandler) {
            window.removeEventListener('pagehide', this._unloadHandler);
          }
          this._unloadHandler = () => {
            console.debug('Page is unloading, closing WebSocket connection.');
            if (
              ws.readyState !== WEBSOCKET_STATES.CLOSED &&
              ws.readyState !== WEBSOCKET_STATES.CLOSING
            ) {
              ws.close(1000, 'page-unload');
            }
          };
          window.addEventListener('pagehide', this._unloadHandler);

          ws.onmessage = ev => {
            const message = JSON.parse(ev?.data);
            console.debug(message);
            this.websocketEventRouter(message);
          };

          ws.onerror = err => {
            console.error(`${JSON.stringify(err)}`);
          };

          ws.onopen = ev => {
            if (this._closing) {
              // Send a proper close frame so the backend fires $disconnect reliably.
              ws.close(1000, 'logout');
              return;
            }

            console.debug('Socket is now open.', ev);

            this.readyState = WEBSOCKET_STATES.OPEN;
            this.loading = false;
            this.keepalive();
            this.backoff = 2000;
          };

          ws.onclose = ev => {
            console.debug('WebSocket connection closed:', ev);
            console.debug('Close code:', ev.code);
            console.debug('Close reason:', ev.reason);

            window.removeEventListener('pagehide', this._unloadHandler);
            this._unloadHandler = undefined;
            this.readyState = WEBSOCKET_STATES.CLOSED;

            if (this._closing) {
              this.connection = undefined;
              return;
            }

            this.loading = false;
            this.reconnect();
          };
        },
        Number(Math.min(this.backoff, this.MAX_BACKOFF))
      );
    },
    async close() {
      console.debug('Closing websocket connection.');
      this._closing = true;
      this.loading = false;
      clearTimeout(this.connectTimeout);
      clearInterval(this.ping);

      if (this._unloadHandler) {
        window.removeEventListener('pagehide', this._unloadHandler);
        this._unloadHandler = undefined;
      }

      const ws = this.connection;
      if (!ws || ws.readyState === WEBSOCKET_STATES.CLOSED) {
        this.connection = undefined;
        this.readyState = undefined;
        return;
      }

      // Return a Promise that resolves only when the socket is actually closed.
      // logOut() awaits this so signOut() (which navigates the page away via
      // window.location.replace) cannot run until after the backend receives
      // the close frame and fires $disconnect — preventing stale ws_connection rows.
      // A 3s timeout acts as a safety net so logout is never blocked indefinitely.
      return new Promise(resolve => {
        const done = () => {
          this.connection = undefined;
          this.readyState = undefined;
          this._closing = false;
          resolve();
        };

        const timeout = setTimeout(() => {
          console.debug('WebSocket close timed out, proceeding with logout.');
          done();
        }, 3000);

        ws.onclose = () => {
          clearTimeout(timeout);
          done();
        };

        if (ws.readyState === WEBSOCKET_STATES.CONNECTING) {
          // Cannot send a close frame while the handshake is in progress.
          // onopen will call ws.close(1000) once the socket is OPEN,
          // which triggers onclose → done() above, resolving this Promise
          // before signOut() navigates the page away.
          console.debug(
            'WebSocket is still connecting — will send close frame after handshake.'
          );
        } else {
          ws.close(1000, 'logout');
        }
      });
    },
  },
});

// Enable Hot Module Replacement (HMR)
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useWebSockets, import.meta.hot));
}
